<template>
  <!-- Billing History Section -->
  <div class="billing-payments__section-wrapper">
    <div class="billing-payments__section-header">
      <h3 class="billing-payments__section-title">Billing History</h3>
      <p class="billing-payments__section-subtitle">View your past invoices and payment history</p>
    </div>

    <div class="billing-payments__section-body">
      <!-- Loading state - now using the combined loading state -->
      <div v-if="isHistoryLoading" class="billing-payments__loading">
        <q-spinner-dots color="primary" size="40px" />
      </div>

      <!-- Empty state -->
      <div v-else-if="billingHistory.length === 0" class="billing-payments__empty">
        <q-icon name="o_receipt_long" size="32px" color="grey-6" class="billing-payments__empty-icon" />
        <p class="billing-payments__empty-title">No billing history available</p>
        <p class="billing-payments__empty-subtitle">Your billing history will appear here once you make a payment</p>
      </div>

      <!-- Billing history table -->
      <div v-else class="billing-payments__table-container">
        <table class="billing-payments__table">
          <thead>
            <tr>
              <th>Ref ID.</th>
              <th>Name</th>
              <th>Amount</th>
              <th>Invoice Period</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="billing in billingHistory" :key="billing.id" class="billing-payments__table-row">
              <td>{{ billing.reference }}</td>
              <td>{{ billing.name }}</td>
              <td>{{ formatCurrency(billing.amount, billing.currency) }}</td>
              <td>{{ formatDateRange(billing.period_start, billing.period_end) }}</td>
              <td class="billing-payments__status-cell">
                <span class="billing-payments__status-badge" :class="`billing-payments__status-badge--${billing.status}`">
                  {{ capitalizeFirstLetter(billing.status) }}
                </span>
              </td>
              <td class="billing-payments__action-cell">
                <div class="billing-payments__actions">
                  <q-btn round flat dense size="sm" icon="more_horiz" class="billing-payments__method-menu">
                    <q-menu anchor="bottom right" self="top right" class="billing-payments__dropdown-menu">
                      <q-list class="billing-payments__dropdown-list">
                        <q-item clickable v-close-popup @click="showBillingDetails(billing)" class="billing-payments__dropdown-item">
                          <q-item-section side class="billing-payments__dropdown-icon">
                            <q-icon name="o_info" color="terziary" />
                          </q-item-section>
                          <q-item-section>
                            <div class="billing-payments__dropdown-item-text">View details</div>
                          </q-item-section>
                        </q-item>
                      </q-list>
                    </q-menu>
                  </q-btn>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="billing-payments__pagination">
          <span class="billing-payments__record-count">{{ billingHistory.length }} records</span>
        </div>
      </div>
    </div>

    <!-- Billing Details Dialog - Now using the dedicated component -->
    <billing-details-dialog v-model="showDetailsDialog" :billing="selectedBilling" @close="closeDetailsDialog" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import BillingDetailsDialog from './BillingDetailsDialog.vue';
import BillingFormat from '@/utils/mixins/billing-format';

export default {
  name: 'BillingHistory',
  components: {
    BillingDetailsDialog
  },
  mixins: [BillingFormat],
  computed: {
    ...mapGetters({
      billingHistory: 'billing/billingHistory',
      isLoading: 'billing/isBillingHistoryLoading',
      isInitialLoading: 'billing/isInitialLoading'
    }),

    // Add a computed property to respect both loading states
    isHistoryLoading() {
      return this.isLoading || this.isInitialLoading;
    }
  },
  data() {
    return {
      showDetailsDialog: false,
      selectedBilling: null
    };
  },
  methods: {
    ...mapActions({
      fetchBillingHistory: 'billing/fetchBillingHistory'
    }),
    // Only fetch history when explicitly called from parent
    refreshBillingHistory() {
      // Don't load automatically - only when explicitly called
      this.fetchBillingHistory();
    },
    showBillingDetails(billing) {
      this.selectedBilling = billing;
      this.showDetailsDialog = true;
    },
    closeDetailsDialog() {
      this.showDetailsDialog = false;
      this.selectedBilling = null;
    }
  }
};
</script>

<style lang="scss">
@import './billing-payments-styles.scss';
</style>
